import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useValidateInviteTokenQuery, useSetPasswordMutation } from '../store/api/authApi'
import toast from 'react-hot-toast'

const SetPassword: React.FC = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const navigate = useNavigate()
  const { data, error, isLoading: isValidating } = useValidateInviteTokenQuery(token, { skip: !token })
  const [setPassword, { isLoading }] = useSetPasswordMutation()
  const [password, setPasswordValue] = useState('')
  const [confirm, setConfirm] = useState('')

  useEffect(() => {
    if (!token) {
      toast.error('Missing invite link')
    }
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }
    if (password !== confirm) {
      toast.error('Passwords do not match')
      return
    }
    try {
      await setPassword({ token, password }).unwrap()
      toast.success('Password set. Please sign in.')
      navigate('/login', { replace: true })
    } catch (err: any) {
      toast.error(err?.data?.message || 'Failed to set password')
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-semibold mb-2">Invalid link</h1>
          <p className="text-gray-600 mb-4">This invite link is missing a token.</p>
          <Link to="/login" className="text-blue-600 hover:underline">Go to sign in</Link>
        </div>
      </div>
    )
  }

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (error || !data?.success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-semibold mb-2">Link expired</h1>
          <p className="text-gray-600 mb-4">This invite or reset link is invalid or already used.</p>
          <Link to="/forgot-password" className="text-blue-600 hover:underline">Request a new link</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-6">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 max-w-md w-full">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Set your admin password</h1>
        <p className="text-gray-600 mb-6">
          Welcome{data.data?.name ? `, ${data.data.name}` : ''}. Create a password for{' '}
          <strong>{data.data?.email}</strong> to open the ThinkClass Admin panel.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPasswordValue(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="At least 6 characters"
              disabled={isLoading}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isLoading}
              required
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : 'Save password & continue'}
          </button>
        </form>
        <p className="mt-4 text-sm text-center text-gray-500">
          Already set a password? <Link to="/login" className="text-blue-600 hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  )
}

export default SetPassword
